import * as React from "react";

import { CollectionItemPageProps } from "../CollectionItemPageProps.csharp";
import { CollectionItemKeyInfo } from "../CollectionItemKeyInformation/CollectionItemKeyInfo";
import { CollectionImages } from "../../CollectionImages/CollectionImages";
import classNames from "classnames";
import { CollectionItemMetadata } from "../CollectionItemMetadata/CollectionItemMetadata";
import { CollectionItemTags } from "../CollectionItemTags/CollectionItemTags";
import { CollectionSearchList } from "../../CollectionSearchPage/CollectionSearchList";
import { CollectionItemPageList } from "../CollectionItemPageList/CollectionItemPageList";

export const CollectionItemPage = (props: CollectionItemPageProps) => {
    const metadataRef = React.useRef<HTMLDivElement>(null);

    const [searchUrlParams, setSearchUrlParams] = React.useState("");
    const [searchQuery, setSearchQuery] = React.useState("");

    React.useEffect(() => {
        const storedStateJSON = sessionStorage.getItem("searchState");
        if (storedStateJSON) {
            const storedState = JSON.parse(storedStateJSON);
            setSearchUrlParams(storedState.searchUrlParams);
            setSearchQuery(storedState.query);
        }
    }, []);

    function getStringForLanguageWithFallback(norwegianString: string, englishString: string, language: string) {
        if (language === "en" && englishString) return englishString;
        else return norwegianString;
    }

    function getDimensionsString(dimensionsString, classification) {
        if (classification === "Fotografier" && dimensionsString.includes("Grafikkmotiv: ")) {
            return dimensionsString.replace("Grafikkmotiv: ", "");
        } else if (classification === "Skulpturer" && dimensionsString.includes("Annet: ")) {
            return dimensionsString.replace("Annet: ", "");
        }
        return dimensionsString;
    }

    function getDimensionsStringEnglish(dimensionsString, classification) {
        dimensionsString = getDimensionsString(dimensionsString, classification);

        const translations = {
            "Bilde:": "Image:",
            "Papir:": "Paper:",
            "Teknisk mål:": "Technical measurement:",
            "Innrammet:": "Framed:",
            "Perm:": "Cover:",
            "Motiv:": "Motif:",
            "Annet:": "Other:",
        };

        Object.keys(translations).forEach((term) => {
            if (dimensionsString.includes(term)) {
                dimensionsString = dimensionsString.replace(term, translations[term]);
            }
        });

        return dimensionsString;
    }

    const collectionItemKeyInfoData = {
        title: props.translations.title,
        medium: props.translations.medium,
        description: getStringForLanguageWithFallback(
            props.collectionObject.description,
            props.collectionObject.descriptionEn,
            props.language
        ),
        displayDate: props.collectionObject.displayDate,
        inventoryNumberTitle: props.translations.inventoryNumber,
        inventoryNumber: props.collectionObject.name,
        catalogueRaisonne: props.collectionObject.catalogueRaisonne,
        onDisplay: {
            text: props.exhibitedIn ?? props.translations.exhibitedIn,
            link:
                props.collectionObject.partOfExhibitions.length !== 0 &&
                getStringForLanguageWithFallback(
                    props.collectionObject.partOfExhibitions[0].exhibitionPageLink,
                    props.collectionObject.partOfExhibitions[0].exhibitionPageLinkEnglish,
                    props.language
                ),
            linkText:
                props.collectionObject.partOfExhibitions.length !== 0 &&
                getStringForLanguageWithFallback(
                    props.collectionObject.partOfExhibitions[0].exhibitionTitle,
                    props.collectionObject.partOfExhibitions[0].exhibitionTitleEnglish,
                    props.language
                ),
        },
        isVirtualObject: props.collectionObject.isVirtualObject,
        numberOfChildren: props.virtualObjectChildren.length,
        virtualObject: {
            //TODO: need new props virtualParentTitle and virtualParentRelationships
            virtualCollectionDescriptionString: props.virtualObject.virtualCollectionDescriptionString,
            virtualCollectionLinkString: props.virtualObject.virtualCollectionLinkString,
            virtualParentInvNo: props.virtualObject.virtualParentInvNo,
            virtualParentRelationships: props.virtualObject.virtualParentRelationships,
            virtualObjectInfoString: props.virtualObjectInfo,
        },
        showMoreDetailsLinkText: props.translations.showMoreDetails,
        aboutObjectText: props.translations.aboutObject,
        classification: props.collectionObject.classification,
    };

    const collectionImagesData = {
        mediaId: props.collectionObject.mediaId,
        id: props.collectionObject.id,
        title: props.collectionObject.title,
        ariaImageExpanded: props.ariaImageExpanded,
        mediaCopyright: props.collectionObject.mediaCopyright,
        copyrightFallback: props.copyrightFallback,
        fotographer: props.collectionObject.fotographer,
        fotographerLabel: props.translations.fotographerLabel,
    };

    const collectionMetaData = [
        {
            label: props.translations.titleLabel,
            value: [
                props.collectionObject.title && props.collectionObject.title + " (NO)",
                props.collectionObject.titleEn && props.collectionObject.titleEn + " (EN)",
                props.collectionObject.titleDe && props.collectionObject.titleDe + " (DE)",
            ],
        },
        {
            label: props.translations.date,
            value: props.collectionObject.displayDate && props.collectionObject.displayDate,
        },
        {
            label: props.translations.classification,
            value:
                props.collectionObject.classification &&
                getStringForLanguageWithFallback(
                    props.collectionObject.classification,
                    props.collectionObject.classificationEn,
                    props.language
                ),
        },
        {
            label: props.translations.printer,
            value:
                props.collectionObject.otherPeople &&
                props.collectionObject.otherPeople.length !== 0 &&
                props.collectionObject.otherPeople.some((item) => item.role === "Trykker")
                ? props.collectionObject.otherPeople
                    .filter((person) => person.role === "Trykker")
                    .map(
                        (person) =>
                            person.name +
                            (person.nationality
                                ? `, ${getStringForLanguageWithFallback(
                                    person.nationality,
                                    "norwegian",
                                    props.language
                                )}`
                                : "")
                    )
                    .join(", ")
                : null,
        },
        props.collectionObject.classification === "Grafikk"
            ? {
                  label: props.translations.technique,
                  value: [
                      getStringForLanguageWithFallback(
                          props.collectionObject.medium,
                          props.collectionObject.mediumEn,
                          props.language
                      ),
                      getStringForLanguageWithFallback(
                          props.collectionObject.paperSupport,
                          props.collectionObject.paperSupportEn,
                          props.language
                      ),
                  ].filter(Boolean),
              }
            : null,
        props.collectionObject.classification === "Tegninger"
            ? {
                  label: props.translations.tools,
                  value: [
                      getStringForLanguageWithFallback(
                          props.collectionObject.medium,
                          props.collectionObject.mediumEn,
                          props.language
                      ),
                      getStringForLanguageWithFallback(
                          props.collectionObject.paperSupport,
                          props.collectionObject.paperSupportEn,
                          props.language
                      ),
                  ].filter(Boolean),
              }
            : null,
        props.collectionObject.classification !== "Grafikk" && props.collectionObject.classification !== "Tegninger"
            ? {
                  label: props.translations.technique,
                  value: getStringForLanguageWithFallback(
                      props.collectionObject.medium,
                      props.collectionObject.mediumEn,
                      props.language
                  ),
              }
            : null,
        {
            label: props.translations.dimensions,
            value:
                props.collectionObject.dimensions &&
                props.collectionObject.dimensions.length > 0 &&
                props.collectionObject.dimensions.some((dim) => dim != null && dim !== "")
                    ? props.language === "en"
                        ? props.collectionObject.dimensions
                              .filter((dim) => dim != null && dim !== "")
                              .map((string) =>
                                  getDimensionsStringEnglish(string, props.collectionObject.classification)
                              )
                              .reduce((acc, string) => acc.concat(string.split(/[;]/).map((part) => part.trim())), [])
                              .filter((part) => part !== "")
                        : props.collectionObject.dimensions
                              .filter((dim) => dim != null && dim !== "")
                              .map((string) => getDimensionsString(string, props.collectionObject.classification))
                              .reduce((acc, string) => acc.concat(string.split(/[;]/).map((part) => part.trim())), [])
                              .filter((part) => part !== "")
                    : null,
        },
        {
            label: props.translations.signed,
            value: props.collectionObject.signed && props.collectionObject.signed,
        },
        {
            label: props.translations.inscribed,
            value: getStringForLanguageWithFallback(
                props.collectionObject.inscribed,
                props.collectionObject.inscribedEn,
                props.language
            ),
        },
        {
            label: props.translations.markings,
            value: props.collectionObject.markings && props.collectionObject.markings,
        },
        {
            label: props.translations.owner,
            value:
                props.collectionObject.creditline &&
                getStringForLanguageWithFallback(
                    props.collectionObject.creditline,
                    props.collectionObject.creditlineEn,
                    props.language
                ),
        },
        {
            label: props.translations.portrayed,
            value:
                !["Fotografier", "Dokumentarfoto"].includes(props.collectionObject.classification) &&
                props.collectionObject.portrayedPeople.length !== 0 &&
                props.collectionObject.portrayedPeople.some((item) => item.role === "Portrettert")
                    ? props.collectionObject.portrayedPeople
                          .filter((person) => person.role === "Portrettert")
                          .map(
                              (person) =>
                                  person.name +
                                  (person.nationality
                                      ? `, ${getStringForLanguageWithFallback(
                                            person.nationality,
                                            "norwegian",
                                            props.language
                                        )}`
                                      : "")
                          )
                          .join(", ")
                    : null,
        },
        {
            label: props.translations.depicted,
            value:
                ["Fotografier", "Dokumentarfoto"].includes(props.collectionObject.classification) &&
                props.collectionObject.portrayedPeople.length > 0
                    ? props.collectionObject.portrayedPeople
                          .map((person) => {
                              const nationality = person.nationality
                                  ? `, ${getStringForLanguageWithFallback(
                                        person.nationality,
                                        "norwegian",
                                        props.language
                                    )}`
                                  : "";
                              return `${person.name}${nationality}`;
                          })
                          .join(", ")
                    : null,
        },
        {
            label: props.translations.model,
            value:
                !["Fotografier", "Dokumentarfoto"].includes(props.collectionObject.classification) &&
                    props.collectionObject.portrayedPeople.length !== 0 &&
                    props.collectionObject.portrayedPeople.some((item) => item.role === "Modell")
                    ? props.collectionObject.portrayedPeople
                        .filter((person) => person.role === "Modell")
                        .map(
                            (person) =>
                                person.name +
                                (person.nationality
                                    ? `, ${getStringForLanguageWithFallback(
                                        person.nationality,
                                        "norwegian",
                                        props.language
                                    )}`
                                    : "")
                        )
                        .join(", ")
                    : null,
        },
        {
            label: props.translations.bibliography,
            value: props.collectionObject.bibliography && props.collectionObject.bibliography,
        },
    ].filter((item) => item && item.value != null && item.value !== "");

    return (
        <div className="CollectionItemPage">
            <div
                className={classNames("CollectionItemPage__top-container", {
                    "CollectionItemPage__top-container--is-virtual-object": props.collectionObject.isVirtualObject,
                })}
            >
                <section className="wrapper">
                    <div className="CollectionItemPage__back">
                        <div className="CollectionItemPage__back-content">
                            <div className="CollectionItemPage__back-arrow" />

                            <a
                                className="CollectionItemPage__back-link"
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (
                                        window.history.length > 1 &&
                                        document.referrer.split("?")[1] === searchUrlParams
                                    ) {
                                        window.history.back();
                                    } else {
                                        window.location.href = `${props.collectionSearchPageUrl}${
                                            searchUrlParams ? `?${searchUrlParams}` : ""
                                        }`;
                                    }
                                }}
                            >
                                {`${props.translations.backTo} `}
                                <span className="CollectionItemPage__back-query-text">
                                    {searchQuery
                                        ? searchQuery.length >= 15
                                            ? `"${searchQuery.slice(0, 12).toLowerCase()}..."`
                                            : `"${searchQuery.toLowerCase()}"`
                                        : `${props.backToLabel}`}
                                </span>
                            </a>
                        </div>
                    </div>
                    <div
                        className={classNames("CollectionItemPage__key-info-container", {
                            "CollectionItemPage__key-info-container--is-virtual-object":
                                props.collectionObject.isVirtualObject,
                        })}
                    >
                        <CollectionItemKeyInfo {...collectionItemKeyInfoData} ref={metadataRef} />
                        {!props.collectionObject.isVirtualObject && <CollectionImages {...collectionImagesData} />}
                        {props.collectionObject.isVirtualObject && props.virtualObjectChildren?.length !== 0 && (
                            <CollectionItemPageList
                                isVirtualObject={true}
                                smallHeading={true}
                                translations={props.translations}
                                items={props.virtualObjectChildren}
                                title={props.individualWorksTitle.replace(
                                    "{0}",
                                    `${props.virtualObjectChildren.length}`
                        )}
                        />
                    )}
                    </div>
                    {Array.isArray(props.sketchbookChildren) && props.sketchbookChildren.length !== 0 && (
                        <CollectionItemPageList
                            isVirtualObject={true}
                            smallHeading={true}
                            translations={props.translations}
                            items={props.sketchbookChildren}
                            title={props.sketchbookPagesTitle.replace(
                                "{0}",
                                `${props.sketchbookChildren?.length}`
                            )}
                        />
                    )}
                </section>
            </div>

            {props.relationships?.length !== 0 && (
                <section className="wrapper">
                    <CollectionItemPageList
                        isVirtualObject={props.collectionObject.isVirtualObject}
                        translations={props.translations}
                        items={props.relationships}
                        title={props.translations.relatedTitle}
                    />
                </section>
            )}

            {props.collectionObject.motifs && props.collectionObject.motifs.length !== 0 && (
                <section className="wrapper">
                    <CollectionItemTags
                        title={props.tagsTitle}
                        description={props.tagsDescription}
                        tags={props.collectionObject.motifs}
                        collectionSearchPageUrl={props.collectionSearchPageUrl}
                    />
                </section>
            )}

            <section className="wrapper">
                <CollectionItemMetadata
                    title={props.translations.details}
                    metadata={collectionMetaData}
                    ref={metadataRef}
                    showMoreLabel={props.translations.showMore}
                    showLessLabel={props.translations.showLess}
                />
            </section>
        </div>
    );
};
